import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'

export type ContactInput = Readonly<{ firstName: string; lastName?: string | undefined; phoneNumber?: string | undefined; emailAddress?: string | undefined; socialMedia?: string | undefined; note?: string | undefined }>
export type RegistrationAnswerInput = Readonly<{ fieldId: string; value: unknown }>
export type ConfirmationStatus = 'confirmed' | 'maybe' | 'unreachable'

/**
 * `session-missing` and `form-changed` can never succeed on retry, so they are
 * handled differently from a plain failure. A closed session still accepts
 * registrations (they may have been taken offline), so only a session that no
 * longer exists is refused. `removed`: the person who registered them removed
 * it, so the device's copy is done with.
 */
export type RegistrationServiceErrorKind = 'offline' | 'invalid' | 'session-missing' | 'form-changed' | 'removed' | 'forbidden' | 'unexpected'

/** Shown when the server refuses because the person no longer has the permission. */
const noPermission = (tab: string) => `You don’t have the ${tab} permission any more, so this wasn’t saved. It’s kept on this phone: ask someone who can manage permissions, then send it again.`

/** 42501 also means "authentication required", which a retry after signing in fixes. */
const isPermissionRefusal = (error: { code?: string; message?: string }) => error.code === '42501' && /permission/i.test(error.message ?? '')

const removedError = () => new RegistrationServiceError('This registration was removed by the person who registered them.', 'removed')

export class RegistrationServiceError extends Error {
  constructor(message: string, public readonly kind: RegistrationServiceErrorKind = 'unexpected') {
    super(message)
    this.name = 'RegistrationServiceError'
  }
}

/** A contact as registered by one collector, with where and when. */
export type RegisteredContact = Readonly<{
  registrationId: string
  capturedAt: string
  sessionId: string
  sessionName: string | null
  firstName: string
  lastName: string | null
  phoneNumber: string | null
  emailAddress: string | null
  socialMedia: string | null
  confirmationStatus: ConfirmationStatus
  ticketReference: string | null
}>

/** A registration in My contacts: a RegisteredContact plus the event it belongs to. */
export type MyContact = RegisteredContact & Readonly<{
  eventId: string
  eventName: string
  /** The event's first day, `YYYY-MM-DD`. */
  eventDate: string | null
  ticketed: boolean
}>

type RegisteredContactRow = {
  id: string
  captured_at: string
  session_id: string
  outreach_sessions: { name: string | null } | null
  contacts: { first_name: string; last_name: string | null; phone_number: string | null; email_address: string | null; social_media: string | null } | null
  confirmation_status: ConfirmationStatus
  tickets: { reference: string } | null
}

type MyContactRow = RegisteredContactRow & {
  event_id: string
  events: { name: string; event_date: string | null; ticketing_mode: string | null } | null
}

/** The API's row limit per request (supabase/config.toml `max_rows`). */
export const allMinePageSize = 1000

const registeredContactColumns = 'id, captured_at, session_id, confirmation_status, outreach_sessions(name), contacts(first_name, last_name, phone_number, email_address, social_media), tickets(reference)'

const myContactColumns = () => `${registeredContactColumns}, event_id, events(name, event_date, ticketing_mode)`

const toMyContact = (row: MyContactRow, contact: NonNullable<RegisteredContactRow['contacts']>): MyContact => ({
  ...toRegisteredContact(row, contact),
  eventId: row.event_id,
  eventName: row.events?.name ?? 'Event',
  eventDate: row.events?.event_date ?? null,
  ticketed: row.events?.ticketing_mode === 'ticketed',
})

const toRegisteredContact = (row: RegisteredContactRow, contact: NonNullable<RegisteredContactRow['contacts']>): RegisteredContact => ({
  registrationId: row.id,
  capturedAt: row.captured_at,
  sessionId: row.session_id,
  sessionName: row.outreach_sessions?.name ?? null,
  firstName: contact.first_name,
  lastName: contact.last_name,
  phoneNumber: contact.phone_number,
  emailAddress: contact.email_address,
  socialMedia: contact.social_media,
  confirmationStatus: row.confirmation_status,
  ticketReference: row.tickets?.reference ?? null,
})


export interface RegistrationService {
  /** `capturedAt` is when the attendee was registered on the device, for registrations synced later. */
  capture(input: { clientRegistrationId: string; sessionId: string; formId: string; contact: ContactInput; answers: RegistrationAnswerInput[]; capturedAt?: string | undefined }): Promise<string>
  /** Registers a walk-up at the event's Box Office and checks them in, in one step. Returns the registration id. */
  registerAtBoxOffice(input: { clientRegistrationId: string; eventId: string; formId: string; contact: ContactInput; answers: RegistrationAnswerInput[]; capturedAt: string; clientCheckInId: string; origin: 'online' | 'offline' }): Promise<string>
  /** Every contact this collector registered at the event, in any of its sessions, newest first. */
  listMine(eventId: string, collectorId: string): Promise<RegisteredContact[]>
  /**
   * Every registration this collector has made, at every event, newest first.
   * Each registration is its own entry: nothing is matched or merged, because
   * people who share a phone number (a couple, a family) are still two people.
   */
  listAllMine(collectorId: string): Promise<MyContact[]>
  /** How many registrations this collector has made, without downloading them. */
  countAllMine(collectorId: string): Promise<number>
  /** One of this collector's registrations, or null if it's gone or isn't theirs. */
  getMine(registrationId: string, collectorId: string): Promise<MyContact | null>
  /** Saves the follow-up outcome for one of this collector's registrations. */
  updateConfirmationStatus(registrationId: string, status: ConfirmationStatus): Promise<ConfirmationStatus>
  /**
   * Removes one of this collector's registrations, with its answers, ticket and
   * check-ins. Removing one that is already gone succeeds.
   */
  remove(registrationId: string): Promise<void>
}

export function createRegistrationService(client: SupabaseClient = supabase): RegistrationService {
  return { async capture(input) {
    if (!navigator.onLine) throw new RegistrationServiceError('A connection is required to save a registration. Your entered details are still here.', 'offline')
    const result = await client.rpc('capture_registration', {
      client_registration_id: input.clientRegistrationId, session_id: input.sessionId, form_id: input.formId,
      // The database reads snake_case keys; it also accepts the older camelCase.
      contact: input.contact, answers: input.answers.map((answer) => ({ field_id: answer.fieldId, value: answer.value })),
      ...(input.capturedAt ? { captured_at: input.capturedAt } : {}),
    }) as unknown as { data: unknown; error: { code?: string; message?: string } | null }
    const { data, error } = result
    if (error) {
      if (error.code === 'P0410') throw removedError()
      if (error.code === 'P0002') throw new RegistrationServiceError('This session no longer exists, so the registration can’t be saved to it.', 'session-missing')
      if (error.code === '40001') throw new RegistrationServiceError('The registration form was updated while you were entering details. Check the form and save again.', 'form-changed')
      if (error.code === '22023') throw new RegistrationServiceError(error.message ?? 'The registration details are invalid.', 'invalid')
      if (isPermissionRefusal(error)) throw new RegistrationServiceError(noPermission('Outreach'), 'forbidden')
      throw new RegistrationServiceError('The registration could not be saved. Please try again.')
    }
    return data as string
  },
  async registerAtBoxOffice(input) {
    if (!navigator.onLine) throw new RegistrationServiceError('A connection is required to send this registration.', 'offline')
    const result = await client.rpc('register_at_box_office', {
      client_registration_id: input.clientRegistrationId, event_id: input.eventId, form_id: input.formId,
      contact: input.contact, answers: input.answers.map((answer) => ({ field_id: answer.fieldId, value: answer.value })),
      captured_at: input.capturedAt, client_check_in_id: input.clientCheckInId, origin: input.origin,
    }) as unknown as { data: Array<{ registration_id: string }> | null; error: { code?: string; message?: string } | null }
    const { data, error } = result
    if (error) {
      if (error.code === 'P0410') throw removedError()
      if (error.code === '40001') throw new RegistrationServiceError('The registration form was updated while you were entering details. Check the form and save again.', 'form-changed')
      if (isPermissionRefusal(error)) throw new RegistrationServiceError(noPermission('Check-in'), 'forbidden')
      if (error.code === '22023') throw new RegistrationServiceError(error.message?.includes('ticketed') ? 'Box Office is only for events that use tickets.' : (error.message ?? 'The registration details are invalid.'), 'invalid')
      throw new RegistrationServiceError('The registration could not be saved. Please try again.')
    }
    const registrationId = data?.[0]?.registration_id
    if (!registrationId) throw new RegistrationServiceError('The registration could not be saved. Please try again.')
    return registrationId
  },
  async listMine(eventId, collectorId) {
    if (!navigator.onLine) throw new RegistrationServiceError('You appear to be offline. Reconnect to see your contacts.', 'offline')
    const result = await client.from('registrations')
      .select(registeredContactColumns)
      .eq('event_id', eventId)
      .eq('registered_by', collectorId)
      .order('captured_at', { ascending: false }) as unknown as { data: RegisteredContactRow[] | null; error: unknown }
    if (result.error) throw new RegistrationServiceError('Your contacts could not be loaded. Check your connection and try again.')
    return (result.data ?? []).flatMap((row) => row.contacts ? [toRegisteredContact(row, row.contacts)] : [])
  },
  async listAllMine(collectorId) {
    if (!navigator.onLine) throw new RegistrationServiceError('You appear to be offline. Reconnect to see your contacts.', 'offline')
    // The API returns at most 1,000 rows a request, so read in pages until a
    // short page: an alphabetical list, its A–Z index, search and the offline
    // copy all need every contact, not just the first thousand.
    const rows: MyContactRow[] = []
    for (let from = 0; ; from += allMinePageSize) {
      const result = await client.from('registrations')
        .select(myContactColumns())
        .eq('registered_by', collectorId)
        .order('captured_at', { ascending: false })
        .order('id', { ascending: true })
        .range(from, from + allMinePageSize - 1) as unknown as { data: MyContactRow[] | null; error: unknown }
      if (result.error) throw new RegistrationServiceError('Your contacts could not be loaded. Check your connection and try again.')
      const page = result.data ?? []
      rows.push(...page)
      if (page.length < allMinePageSize) break
    }
    return rows.flatMap((row) => row.contacts ? [toMyContact(row, row.contacts)] : [])
  },
  async countAllMine(collectorId) {
    if (!navigator.onLine) throw new RegistrationServiceError('You appear to be offline. Reconnect to see your contacts.', 'offline')
    const result = await client.from('registrations').select('id', { count: 'exact', head: true }).eq('registered_by', collectorId) as unknown as { count: number | null; error: unknown }
    if (result.error) throw new RegistrationServiceError('Your contacts could not be counted.')
    return result.count ?? 0
  },
  async getMine(registrationId, collectorId) {
    if (!navigator.onLine) throw new RegistrationServiceError('You appear to be offline. Reconnect to see this contact.', 'offline')
    const result = await client.from('registrations').select(myContactColumns()).eq('id', registrationId).eq('registered_by', collectorId).maybeSingle() as unknown as { data: MyContactRow | null; error: unknown }
    if (result.error) throw new RegistrationServiceError('This contact could not be loaded. Check your connection and try again.')
    return result.data?.contacts ? toMyContact(result.data, result.data.contacts) : null
  },
  async updateConfirmationStatus(registrationId, status) {
    if (!navigator.onLine) throw new RegistrationServiceError('Reconnect to update this confirmation status.', 'offline')
    const result = await client.rpc('set_registration_confirmation_status', {
      registration_id: registrationId,
      requested_status: status,
    }) as unknown as { data: ConfirmationStatus | null; error: { code?: string; message?: string } | null }
    if (result.error) {
      if (result.error.code === 'P0002') throw new RegistrationServiceError('This registration is no longer available.', 'session-missing')
      throw new RegistrationServiceError('The confirmation status could not be saved. Please try again.')
    }
    return result.data ?? status
  },
  async remove(registrationId) {
    if (!navigator.onLine) throw new RegistrationServiceError('Reconnect to remove this registration.', 'offline')
    const { error } = await client.rpc('remove_registration', { registration_id: registrationId }) as unknown as { error: { code?: string; message?: string } | null }
    if (!error || error.code === 'P0002') return
    if (error.code === '42501') throw new RegistrationServiceError('Only the person who registered them can remove this registration.', 'forbidden')
    throw new RegistrationServiceError('The registration could not be removed. Please try again.')
  } }
}
export const registrationService = createRegistrationService()
